import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { getMigrationProgress, getMigrationClientsByWeek } from '../../services/apiService';
import { onMigrationDataUpdated } from './migrationRefreshEvents';

const COLORS = ['#1D4434', '#D74B00', '#2E7D32', '#1565C0', '#6A1B9A', '#00838F'];

/**
 * MigrationTypeTab - Progress card, trend chart, and by-datacenter chart for one migration type (Azure, CIAM, ISheet)
 */
const SINCE_JAN_2025 = '2025-01-01';
const SINCE_JAN_2025_MONTH = '2025-01';

/**
 * Parse an ISO "YYYY-MM-DD" string into a Date at local midnight of those exact
 * year/month/day parts. We deliberately avoid `new Date("YYYY-MM-DDT00:00:00Z")`
 * because UTC midnight falls on the previous day in timezones west of UTC, which
 * made a Monday-anchored week key like "2025-04-07" render as "6 Apr" and misled
 * users clicking the bar (the server correctly returned Mon–Sun of that week,
 * including e.g. Fri 11 Apr, so the label and contents looked inconsistent).
 */
function isoDayToLocalDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const dayOnly = iso.slice(0, 10);
  const [y, m, d] = dayOnly.split('-').map(Number);
  if (!y || !m || !d) return null;
  const local = new Date(y, m - 1, d);
  return Number.isNaN(local.getTime()) ? null : local;
}

/** Pretty-format an ISO "YYYY-MM-DD" week anchor as "d MMM yyyy" for chart labels. */
function formatWeekAxis(weekIso) {
  if (!weekIso) return '';
  const d = isoDayToLocalDate(weekIso);
  if (!d) return weekIso;
  return format(d, 'd MMM yyyy');
}

/**
 * Format a Monday-anchored week key as a human-friendly range, e.g. "7–13 Apr 2025"
 * (or "28 Apr–4 May 2025" when the week spans months). Used in tooltips and the
 * drill-down dialog so it's obvious the bar represents a 7-day bucket, not a day.
 */
function formatWeekRange(weekIso) {
  const start = isoDayToLocalDate(weekIso);
  if (!start) return weekIso || '';
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) return `${format(start, 'd')}–${format(end, 'd MMM yyyy')}`;
  if (sameYear) return `${format(start, 'd MMM')}–${format(end, 'd MMM yyyy')}`;
  return `${format(start, 'd MMM yyyy')}–${format(end, 'd MMM yyyy')}`;
}

/** Format a possibly-UTC migration/registration date using its UTC day parts. */
function formatMigrationDay(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const d = isoDayToLocalDate(value);
    if (d) return format(d, 'd-MMM-yyyy');
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const local = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
  return format(local, 'd-MMM-yyyy');
}

/** Pretty-format an ISO "YYYY-MM" month key as "MMM yyyy" for chart labels. */
function formatMonthAxis(monthIso) {
  if (!monthIso) return '';
  const [y, m] = String(monthIso).split('-').map(Number);
  if (!y || !m) return monthIso;
  const local = new Date(y, m - 1, 1);
  if (Number.isNaN(local.getTime())) return monthIso;
  return format(local, 'MMM yyyy');
}

const MigrationTypeTab = ({ type = 'azure', title }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFromStart, setShowFromStart] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalClients, setModalClients] = useState([]);
  const [modalTitle, setModalTitle] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMigrationProgress();
      if (res?.success && res?.data) {
        setProgress({ ...res.data, meta: res.meta });
      } else {
        setProgress(null);
      }
    } catch (err) {
      console.error('Migration progress load error:', err);
      setError(err.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => onMigrationDataUpdated(() => loadData()), [loadData]);

  const handleBarClick = useCallback(async (week, segment, datacenter = '') => {
    if (!week) return;
    const segmentLabel = segment === 'migrated' ? 'Migrated' : segment === 'new' ? 'Newly onboarded' : datacenter || 'All';
    setModalTitle(`Week of ${formatWeekRange(week)} – ${segmentLabel}`);
    setModalOpen(true);
    setModalLoading(true);
    setModalClients([]);
    try {
      const res = await getMigrationClientsByWeek(week, type, segment, datacenter);
      setModalClients(res?.data ?? []);
    } catch (err) {
      console.error('List-by-week error:', err);
      setModalClients([]);
    } finally {
      setModalLoading(false);
    }
  }, [type]);

  const handleExportToExcel = useCallback(() => {
    if (!modalClients.length) return;
    const dateStr = (d) => {
      const s = formatMigrationDay(d);
      return s || '—';
    };
    const rows = modalClients.map((row) => ({
      'Client name': row.instanceName || '—',
      URL: row.instanceUrl || '—',
      Datacenter: row.currentDatacenter || '—',
      'Source DC': row.sourceDatacenter || '—',
      'Migrated / registration date': row.migrationDate ? dateStr(row.migrationDate) : row.registrationDate ? dateStr(row.registrationDate) : '—',
      Type: row.type || '—'
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    const safeTitle = (modalTitle || 'export').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 50);
    XLSX.writeFile(wb, `${safeTitle || 'migration-clients'}.xlsx`);
  }, [modalClients, modalTitle]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={280} p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  const dataKey = type === 'azure' ? 'azure' : type === 'ciam' ? 'ciam' : 'isheet';
  const series = progress?.[dataKey] || {};
  const overall = series.overall || [];
  const migratedOverall = series.migratedOverall || [];
  const newOverall = series.newOverall || [];
  const byDatacenter = series.byDatacenter || {};
  const meta = progress?.meta || {};
  const total = meta.rowCount || 0;
  const migratedCount = series.migratedCount ?? 0;
  const newCount = series.newCount ?? 0;
  const totalWithInitiative = series.totalWithInitiative ?? (migratedCount + newCount);

  // Trend chart: migration only (Newly onboarded removed from this chart)
  const weekSet = new Set([
    ...overall.map((o) => o.week),
    ...migratedOverall.map((o) => o.week)
  ]);
  const sortedWeeks = [...weekSet].sort();
  const trendData = sortedWeeks.map((week) => {
    const o = overall.find((x) => x.week === week);
    const mo = migratedOverall.find((x) => x.week === week);
    return {
      week,
      count: o?.count ?? 0,
      cumulative: o?.cumulative ?? 0,
      Migrated: mo?.count ?? 0
    };
  });

  // By datacenter: build array of { week, dc1, dc2, ... } for stacked/grouped view
  const dcNames = Object.keys(byDatacenter).sort();
  const byDcChartData = trendData.map((row) => {
    const point = { week: row.week };
    dcNames.forEach((dc) => {
      const arr = byDatacenter[dc] || [];
      const match = arr.find((r) => r.week === row.week);
      point[dc] = match?.count ?? 0;
    });
    return point;
  });

  // Default: show trend since Jan 2025; option to show from start
  const filteredTrendData = showFromStart ? trendData : trendData.filter((r) => r.week >= SINCE_JAN_2025);
  const filteredByDcChartData = showFromStart ? byDcChartData : byDcChartData.filter((r) => r.week >= SINCE_JAN_2025);

  // Azure-only: monthly presence (azure vs colo)
  const monthlyDataRaw = type === 'azure' && progress?.azurePresenceMonthly
    ? progress.azurePresenceMonthly.map((m) => ({
        month: m.month,
        Azure: m.azure,
        'On-prem/Colo': m.colo
      }))
    : [];
  const monthlyData = showFromStart ? monthlyDataRaw : monthlyDataRaw.filter((m) => m.month >= SINCE_JAN_2025_MONTH);

  const totalLabel = type === 'azure' ? 'Clients on Azure' : type === 'ciam' ? 'Clients on CIAM' : 'Clients on Backend ISheet';

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ height: 4, bgcolor: '#1D4434' }} />
            <CardContent sx={{ pt: 2, pb: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 0.8 }}>
                Migrated
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#1D4434' }}>
                {migratedCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ height: 4, bgcolor: '#2E7D32' }} />
            <CardContent sx={{ pt: 2, pb: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 0.8 }}>
                New
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#2E7D32' }}>
                {newCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ height: 4, bgcolor: '#D74B00' }} />
            <CardContent sx={{ pt: 2, pb: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 0.8 }}>
                {totalLabel}
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#D74B00' }}>
                {totalWithInitiative.toLocaleString()}
                <Typography component="span" variant="body2" fontWeight={400} color="text.secondary" sx={{ ml: 0.5 }}>
                  / {total.toLocaleString()}
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 3, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1D4434' }}>
          Migration trend (weekly){!showFromStart ? ' — since Jan 2025' : ''}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showFromStart}
              onChange={(e) => setShowFromStart(e.target.checked)}
              size="small"
              sx={{ color: '#1D4434', '&.Mui-checked': { color: '#1D4434' } }}
            />
          }
          label={<Typography variant="body2">Show full history</Typography>}
        />
      </Box>
      <Paper sx={{ p: 2, mb: 3, height: 320 }}>
        {filteredTrendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={filteredTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11 }}
                tickFormatter={formatWeekAxis}
                minTickGap={16}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(label) => `Week of ${formatWeekRange(label)}`} />
              <Legend />
              <Bar
                dataKey="Migrated"
                fill="#1D4434"
                name="Migrated (weekly)"
                radius={[2, 2, 0, 0]}
                onClick={(data) => {
                  const p = data?.payload ?? data;
                  if ((p?.Migrated ?? 0) > 0 && p?.week) handleBarClick(p.week, 'migrated');
                }}
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey="Migrated" position="top" fontSize={10} formatter={(v) => (v > 0 ? v : '')} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height={280} color="text.secondary">
            No trend data yet. Upload migration data to see trends.
          </Box>
        )}
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: '#1D4434' }}>
        By source datacenter (Current_Datacenter){!showFromStart ? ' — since Jan 2025' : ''}
      </Typography>
      <Paper sx={{ p: 2, mb: 3, height: 320 }}>
        {dcNames.length > 0 && filteredByDcChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={filteredByDcChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11 }}
                tickFormatter={formatWeekAxis}
                minTickGap={16}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(label) => `Week of ${formatWeekRange(label)}`} />
              <Legend />
              {dcNames.map((dc, i) => (
                <Bar
                  key={dc}
                  dataKey={dc}
                  stackId="dc"
                  fill={COLORS[i % COLORS.length]}
                  radius={[0, 0, 0, 0]}
                  onClick={(data) => {
                    const p = data?.payload ?? data;
                    if ((p?.[dc] ?? 0) > 0 && p?.week) handleBarClick(p.week, 'all', dc);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <LabelList dataKey={dc} position="top" fontSize={10} formatter={(v) => (v != null && v > 0 ? v : '')} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height={280} color="text.secondary">
            No datacenter breakdown yet.
          </Box>
        )}
      </Paper>

      {type === 'azure' && monthlyData.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2, color: '#1D4434' }}>
            Azure vs on-prem presence (monthly){!showFromStart ? ' — since Jan 2025' : ''}
          </Typography>
          <Paper sx={{ p: 2, height: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatMonthAxis}
                  minTickGap={16}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={formatMonthAxis} />
                <Legend />
                <Line type="monotone" dataKey="Azure" stroke="#1D4434" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="Azure" position="top" fontSize={10} formatter={(v) => (v != null ? v : '')} />
                </Line>
                <Line type="monotone" dataKey="On-prem/Colo" stroke="#D74B00" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="On-prem/Colo" position="top" fontSize={10} formatter={(v) => (v != null ? v : '')} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
          <Typography variant="h6">{modalTitle}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {modalClients.length > 0 && !modalLoading && (
              <Button
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                sx={{ color: '#1D4434', borderColor: '#1D4434', '&:hover': { borderColor: '#153328', bgcolor: 'rgba(29, 68, 52, 0.08)' } }}
                variant="outlined"
              >
                Export to Excel
              </Button>
            )}
            <IconButton size="small" onClick={() => setModalOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {modalLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={32} />
            </Box>
          ) : modalClients.length === 0 ? (
            <Typography color="text.secondary">No clients for this selection.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Client name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Datacenter</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source DC</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Migrated / registration date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalClients.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.instanceName || '—'}</TableCell>
                      <TableCell>
                        {row.instanceUrl ? (
                          <Link href={row.instanceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                            {row.instanceUrl}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{row.currentDatacenter || '—'}</TableCell>
                      <TableCell>{row.sourceDatacenter || '—'}</TableCell>
                      <TableCell>
                        {row.migrationDate
                          ? formatMigrationDay(row.migrationDate)
                          : row.registrationDate
                            ? formatMigrationDay(row.registrationDate)
                            : '—'}
                      </TableCell>
                      <TableCell>{row.type || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MigrationTypeTab;
